import React from 'react';
import { getIntl } from '../../../../../../utils/intl';
import { useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';
import useUserHasOwnMedicalProfile from '../../../../../../hooks/useUserHasOwnMedicalProfile';
import getName from '../../../../../../utils/getName';
import { useLoadedUser } from '../../../../../../hooks/api/useUser';
import useMedicalProfiles from '../../../../../../hooks/api/useMedicalProfiles';
import { usePromptButton } from '../../../../../shared/PromptButton';
import * as Linking from 'expo-linking';
import { supportEmail } from '../../../../../../utils/constants';
import useNotify from '../../../../../../hooks/useNotify';
import { Action } from '../../../../../v2BaseComponents/ActionButtonLayout/types';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import Text from '../../../../../v2BaseComponents/Text';
import { PromptActions } from '../../../../../v2BaseComponents/Prompt';
import { useCanAddMedicalProfile } from '../../../../../../hooks/api/useCanAddMedicalProfile';

const intl = getIntl('mainApp.profiles', {
    addProfile: 'Add Medical Profile',
    promptHeader: 'Who is this Medical Profile for?',
    promptSubtext:
        'You do not have your own Medical Profile created yet. If you click "For Me", we will enter all the information you provided during registration.',
    confirmationPretext: 'For Me - ',
    forSomeoneElse: 'For Someone Else',

    familyLimitHeader: 'You’ve Reached the Limit',
    familyLimitContent:
        'You have used all available Medical Profiles. If you have a need to manage more, please contact us via email.',
    cancel: 'Cancel',
    email: 'Email',
    subscribe: 'Subscribe',
    emailSupportError: 'Unable to open email app',
});

export type UseAddProfileButtonActionReturnValue = {
    addProfileAction: Action;
    Prompt: React.ReactNode;
};

export const useAddProfileButtonAction = (): UseAddProfileButtonActionReturnValue => {
    const notify = useNotify();
    const { isLoadingProfiles } = useMedicalProfiles({});
    const { canAddMedicalProfile, isLoadingCanAddMedicalProfile } = useCanAddMedicalProfile();
    const navigator = useProfilesTabNavigation();
    const userHasMedicalProfile = useUserHasOwnMedicalProfile();
    const { user } = useLoadedUser({});

    const baseActionProps = {
        fullWidth: true,
        title: intl('addProfile'),
        icon: 'PlusCircle' as const,
        isDisabled: isLoadingProfiles || isLoadingCanAddMedicalProfile,
        testID: 'add-profile-button',
    };

    const standardOnPress = () => {
        navigator.navigate('add', { useUser: 'false' });
    };

    const createOwnProfilePrompt = usePromptButton({
        promptTitle: intl('promptHeader'),
        renderPromptContents: (onClose) => (
            <ContentStack>
                <Text variant="body-1">{intl('promptSubtext')}</Text>
                <PromptActions
                    actions={[
                        {
                            title: `${intl('confirmationPretext')}${getName(
                                {
                                    firstName: user.preferredName || user.firstName,
                                    lastName: user.lastName,
                                },
                                {
                                    abbreviateLastName: true,
                                },
                            )}`,
                            onPress: () => {
                                navigator.navigate('add', { useUser: 'true' });
                                onClose();
                            },
                            icon: 'User',
                            testID: 'add-profile-button-for-me-button',
                        },
                        {
                            title: intl('forSomeoneElse'),
                            onPress: () => {
                                navigator.navigate('add', { useUser: 'false' });
                                onClose();
                            },
                            icon: 'Users',
                            testID: 'add-profile-button-for-someone-else-button',
                        },
                    ]}
                />
            </ContentStack>
        ),
    });

    const profileLimitPrompt = usePromptButton({
        promptTitle: intl('familyLimitHeader'),
        renderPromptContents: (onClose) => (
            <ContentStack>
                <Text variant="body-1">{intl('familyLimitContent')}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('email'),
                            onPress: () => {
                                onClose();
                                Linking.openURL(`mailto:${encodeURIComponent(supportEmail)}`).catch(() => {
                                    notify('error', intl('emailSupportError'));
                                });
                            },
                            icon: 'Send',
                        },
                        {
                            title: intl('cancel'),
                            onPress: onClose,
                            icon: 'XCircle',
                            testID: 'add-profile-limit-button-cancel',
                        },
                    ]}
                />
            </ContentStack>
        ),
    });

    const getReturnValue = (): UseAddProfileButtonActionReturnValue => {
        if (!canAddMedicalProfile) {
            return {
                addProfileAction: {
                    ...baseActionProps,
                    onPress: profileLimitPrompt.onPress,
                },
                Prompt: profileLimitPrompt.Prompt,
            };
        } else if (userHasMedicalProfile) {
            return {
                addProfileAction: {
                    ...baseActionProps,
                    onPress: standardOnPress,
                },
                Prompt: null,
            };
        } else {
            return {
                addProfileAction: {
                    ...baseActionProps,
                    onPress: createOwnProfilePrompt.onPress,
                },
                Prompt: createOwnProfilePrompt.Prompt,
            };
        }
    };

    return getReturnValue();
};
